import { useNavigate } from "react-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { Button, Flex, Icon, Icons, Menu, MenuItemProps, Paragraph, useToast } from "@ir/ui";
import { Deduction } from "@/core/entities/deduction";
import { DeductionControlAction, DeductionControlType } from "@/core/entities/deduction/deduction/DeductionControls";
import { useDeductionDrawer, useRemittance, useRemittanceItem, useRefreshDeductionWorkspace, useDeductionEdit } from "@/presentation/deduction/hooks";
import { WORKSPACE_ROUTES } from "@/routes/workspace/utils/Workspace.router.utils";
import { useApproval } from "@/presentation/pages/approvals/hooks";
import { groupByCheckAtom } from "@/store/deduction/main";
import HasAnyPermission from "@/presentation/workspace/HasAnyPermission";
import { WorkspacePermissions } from "@/core/permissions";
import { useConfirm, useUserPermissions } from "@/hooks";
import { DeductionEditMode } from "@/store/deduction/deduction";
import { RaiseDisputeModal } from "./DeductionInformation/Dispute/RaiseDisputeModal";
import { ResponseModal } from "@/presentation/ui/components/common";
import SendForApprovalModal from "./DeductionInformation/Approval/SendForApprovalModal";
import { ValidateDisputeReadinessUseCase } from "@/core/usecases/deduction";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { DeductionDisputeRepository } from "@/infrastructure/repositories/deduction/DeductionDispute.repository";
import { getRemittanceItemNetAmount } from "@/presentation/deduction/utils/getRemittanceItemNetAmount";
import { createLogger } from "@/infrastructure/services/logger";

const disputeClient = GraphQlAdapter.getInstance().getClient();
const disputeRepository = new DeductionDisputeRepository(disputeClient);

interface DeductionDrawerActionsProps {
  deduction: Deduction;
}

const ACTION_LABELS: Record<DeductionControlAction, string> = {
  [DeductionControlAction.APPROVE]: "Approve",
  [DeductionControlAction.AWAITING_RESPONSE]: "Hold for Response",
  [DeductionControlAction.DISPUTE_APPROVED]: "Dispute Approved",
  [DeductionControlAction.DISPUTE_NEEDED]: "Flag for Dispute",
  [DeductionControlAction.DISPUTE_REJECTED]: "Dispute Rejected",
  [DeductionControlAction.EDIT]: "Edit",
  [DeductionControlAction.PUSH_TO_BOOKS]: "Sync with Accounts",
  [DeductionControlAction.RAISE_DISPUTE]: "Raise Dispute",
  [DeductionControlAction.REOPEN]: "Re-open",
  [DeductionControlAction.RETURN]: "Return to Analyst",
  [DeductionControlAction.SUBMIT_FOR_APPROVAL]: "Send for Approval",
  [DeductionControlAction.VOID]: "Void",
  [DeductionControlAction.WRITE_OFF]: "Write Off",
};

const STATUS_ACTION_KEYS: Partial<Record<DeductionControlAction, string>> = {
  [DeductionControlAction.AWAITING_RESPONSE]: "AWAITING_RESPONSE",
  [DeductionControlAction.DISPUTE_NEEDED]: "DISPUTE_NEEDED",
  [DeductionControlAction.DISPUTE_APPROVED]: "DISPUTE_APPROVED",
  [DeductionControlAction.DISPUTE_REJECTED]: "DISPUTE_REJECTED",
  [DeductionControlAction.VOID]: "VOID",
  [DeductionControlAction.WRITE_OFF]: "WRITTEN_OFF",
};

const ACTION_REQUIRED_PERMISSIONS: Partial<Record<DeductionControlAction, string[]>> = {
  [DeductionControlAction.AWAITING_RESPONSE]: [WorkspacePermissions.DEDUCTIONS_STATUS_AWAITING_RESPONSE],
  [DeductionControlAction.DISPUTE_NEEDED]: [WorkspacePermissions.DEDUCTIONS_STATUS_DISPUTE_NEEDED],
  [DeductionControlAction.DISPUTE_APPROVED]: [WorkspacePermissions.DEDUCTIONS_DISPUTE_APPROVE],
  [DeductionControlAction.DISPUTE_REJECTED]: [WorkspacePermissions.DEDUCTIONS_DISPUTE_REJECT],
  [DeductionControlAction.RAISE_DISPUTE]: [WorkspacePermissions.DEDUCTIONS_DISPUTE],
  [DeductionControlAction.SUBMIT_FOR_APPROVAL]: [WorkspacePermissions.DEDUCTIONS_SEND_APPROVAL],
  [DeductionControlAction.APPROVE]: [WorkspacePermissions.DEDUCTIONS_APPROVAL_ACTION],
  [DeductionControlAction.RETURN]: [WorkspacePermissions.DEDUCTIONS_APPROVAL_ACTION],
  [DeductionControlAction.REOPEN]: [WorkspacePermissions.DEDUCTIONS_STATUS_REOPEN],
  [DeductionControlAction.PUSH_TO_BOOKS]: [
    WorkspacePermissions.ACCOUNTING_CREDIT_MEMO_PUSH,
    WorkspacePermissions.ACCOUNTING_PUSH,
  ],
  [DeductionControlAction.VOID]: [WorkspacePermissions.DEDUCTIONS_STATUS_VOID],
  [DeductionControlAction.WRITE_OFF]: [WorkspacePermissions.DEDUCTIONS_STATUS_WRITTEN_OFF],
  [DeductionControlAction.EDIT]: [WorkspacePermissions.DEDUCTIONS_EDIT],
};

const DeductionDrawerActions = ({ deduction }: DeductionDrawerActionsProps) => {
  const logger = createLogger("DeductionDrawerActions");
  const { isDeductionsLoading, drawerInfo, getDeductions, hasAnyUnsavedChanges, setHasAnyUnsavedChanges } = useDeductionDrawer();
  const { handleExpandRemittance, currentExpandedRemittanceKey, remittances } = useRemittance();
  const { remittanceItems } = useRemittanceItem();
  const refreshDeductionWorkspace = useRefreshDeductionWorkspace();
  const groupByCheck = useAtomValue(groupByCheckAtom);
  const {
    resetApprovalWorkflow,
    setIsApprovalModalOpen,
    setApprovalAction,
    setSelectedApproval,
    setSelectedApprovals,
  } = useApproval();
  const { setDeductionIntelligence, patchDeductionIntelligence, getStages, stages, editMode, setEditMode } = useDeductionEdit();
  const confirm = useConfirm();
  const { error: errorToast } = useToast();

  const [isSendForApprovalModalOpen, setIsSendForApprovalModalOpen] = useState(false);
  const [isDisputeModalOpen, setIsDisputeModalOpen] = useState(false);
  const [isDisputeConfirmationOpen, setIsDisputeConfirmationOpen] = useState(false);
  const [isCheckingDisputeReadiness, setIsCheckingDisputeReadiness] = useState(false);
  const isDisputeConfirmationOpenRef = useRef(false);
  const pendingDisputeRefreshRef = useRef(false);
  const navigate = useNavigate();
  const { hasAnyPermission } = useUserPermissions();

  const hasPermissionForAction = useCallback(
    (action: DeductionControlAction) => {
      const required = ACTION_REQUIRED_PERMISSIONS[action];
      if (!required?.length) {
        return true;
      }
      return hasAnyPermission(required);
    },
    [hasAnyPermission]
  );

  const handleDisputeSuccess = useCallback(() => {
    refreshDeductionWorkspace(true);
    getDeductions(true);
    if (currentExpandedRemittanceKey && groupByCheck) {
      handleExpandRemittance(currentExpandedRemittanceKey);
    }
  }, [refreshDeductionWorkspace, getDeductions, currentExpandedRemittanceKey, groupByCheck, handleExpandRemittance]);

  const handleDisputeSubmitted = useCallback(() => {
    isDisputeConfirmationOpenRef.current = true;
    setIsDisputeConfirmationOpen(true);
  }, []);

  const handleDisputeConfirmationClose = useCallback(() => {
    isDisputeConfirmationOpenRef.current = false;
    setIsDisputeConfirmationOpen(false);

    if (pendingDisputeRefreshRef.current) {
      pendingDisputeRefreshRef.current = false;
      handleDisputeSuccess();
    }
  }, [handleDisputeSuccess]);

  const handleDisputeStatusUpdate = useCallback(() => {
    if (isDisputeConfirmationOpenRef.current) {
      pendingDisputeRefreshRef.current = true;
      return;
    }

    handleDisputeSuccess();
  }, [handleDisputeSuccess]);

  const isCurrentRoute = useCallback((route: string) => {
    if (globalThis.window === undefined) {
      return false;
    }
    const pathname = globalThis.window.location.pathname;
    return pathname === route || pathname.endsWith(route) || pathname.includes(`${route}/`);
  }, []);

  const controls = useMemo(
    () =>
      !isDeductionsLoading && Boolean(deduction?.id)
        ? deduction.controls.filter(control => {
          if (!hasPermissionForAction(control.action)) {
            return false;
          }
            if (control.action === DeductionControlAction.RAISE_DISPUTE || control.action === DeductionControlAction.DISPUTE_NEEDED) {
            return deduction.validityKey === "INVALID" || deduction.validityKey === "PARTIALLY_INVALID";
          }
          if (control.action === DeductionControlAction.PUSH_TO_BOOKS) {
            return !isCurrentRoute(WORKSPACE_ROUTES.ACCOUNTING);
          }
          return true;
        })
        : [],
    [deduction, isDeductionsLoading, hasPermissionForAction, isCurrentRoute]
  );

  const updateDeductionStatus = useCallback(
    async (deduction: Deduction, statusKey: string) => {
      await getStages();
      setDeductionIntelligence({
        deduction_id: deduction?.id,
        priority_id: deduction?.priorityId,
        validity_id: deduction?.validityId,
        is_predicted_priority: deduction?.isPredictedPriority,
        stage_id: stages?.data?.find(stage => stage.stageKey === statusKey)?.id ?? null,
      });

      const updatedDeduction = await patchDeductionIntelligence();
      if (updatedDeduction?.id) {
        refreshDeductionWorkspace(true);
        getDeductions(true);
      }
    },
    [getStages, setDeductionIntelligence, stages?.data, patchDeductionIntelligence, refreshDeductionWorkspace, getDeductions]
  );

  const handleReOpen = useCallback(
    async (deductionId: number) => {
      const isReset = await resetApprovalWorkflow(deductionId);
      if (isReset) {
        getDeductions(true);
        refreshDeductionWorkspace(true);
      }
    },
    [resetApprovalWorkflow, refreshDeductionWorkspace, getDeductions]
  );

  const handleEditIntelligence = useCallback(async () => {
    if (editMode === DeductionEditMode.DETAILS && hasAnyUnsavedChanges) {
      const confirmResult = await confirm({
        title: "Unsaved Changes",
        message: "You have unsaved updates. Leave without saving?",
        confirmText: "Discard",
        cancelText: "Go Back",
      });
      if (!confirmResult) {
        return;
      }
      setHasAnyUnsavedChanges(false);
    }
    setEditMode(DeductionEditMode.INTELLIGENCE);
  }, [confirm, editMode, hasAnyUnsavedChanges, setEditMode, setHasAnyUnsavedChanges]);

  const handleIntegratedDisputeRaise = useCallback(async () => {
    setIsCheckingDisputeReadiness(true);
    try {
      const remittanceNetAmount = getRemittanceItemNetAmount(deduction.remittanceItemId, remittanceItems, remittances);
      const backupDocumentCount = deduction.documents.filter(
        doc => doc.documentTypeKey.toLowerCase() === "backup"
      ).length;

      const readiness = await new ValidateDisputeReadinessUseCase(disputeRepository).execute({
        deductionId: deduction.id,
        remittanceItemId: deduction.remittanceItemId,
        purchaserKey: deduction.purchaserKey,
        purchaserId: deduction.purchaserId,
        deductionTypeId: deduction.deductionTypeId,
        checkNumber: deduction.checkNumber || drawerInfo?.checkNumber || "",
        invoiceNumber: deduction.claimNumber || drawerInfo?.invoiceNumber || "",
        deductionAmount: deduction.totalAmount ?? deduction.amount,
        remittanceNetAmount,
        backupDocumentCount,
      });

      if (!readiness.isReady) {
        await confirm({
          alertOnly: true,
          title: "Missing Required Information",
          message: readiness.message,
        });
        return;
      }

      setIsDisputeModalOpen(true);
    } catch (error) {
      logger.error("Failed to validate dispute readiness", error);
      errorToast({
        content: "Failed to fetch dispute types",
        icon: <Icon name={Icons.AlertCircle} />,
      });
    } finally {
      setIsCheckingDisputeReadiness(false);
    }
  }, [confirm, deduction, drawerInfo?.checkNumber, drawerInfo?.invoiceNumber, errorToast, logger, remittanceItems, remittances]);

  const handleAction = useCallback(
    async (action: DeductionControlAction) => {
      setSelectedApproval(null);
      switch (action) {
        case DeductionControlAction.EDIT:
          await handleEditIntelligence();
          break;
        case DeductionControlAction.RAISE_DISPUTE:
          if (deduction.canDispute) {
            await handleIntegratedDisputeRaise();
            break;
          }
          {
            const purchaser = deduction.purchaser;
            const confirmed = await confirm({
              title: "Manual Dispute Required",
              header: (
                <Paragraph className="m-0 mt-2 text-md text-grey-600">
                  {`${purchaser} disputes must be raised directly - integration is not yet available.`}
                </Paragraph>
              ),
              message: `Have you already raised this dispute on the ${purchaser} portal or via email?`,
              confirmText: "Yes, Dispute Has Been Raised",
              confirmButtonVariant: "solid",
              confirmButtonColor: "primary",
              cancelText: "Not Yet - I'll Do It First",
              cancelButtonVariant: "outline",
              cancelButtonColor: "primary",
            });
            if (confirmed) {
              await updateDeductionStatus(deduction, "DISPUTE_RAISED");
            }
          }
          break;
        case DeductionControlAction.SUBMIT_FOR_APPROVAL:
          setIsSendForApprovalModalOpen(true);
          break;
        case DeductionControlAction.APPROVE:
          setSelectedApprovals([deduction]);
          setSelectedApproval(deduction);
          setApprovalAction("APPROVE");
          setIsApprovalModalOpen(true);
          break;
        case DeductionControlAction.RETURN:
          setSelectedApprovals([deduction]);
          setSelectedApproval(deduction);
          setApprovalAction("REJECT");
          setIsApprovalModalOpen(true);
          break;
        case DeductionControlAction.PUSH_TO_BOOKS:
          if (!isCurrentRoute(WORKSPACE_ROUTES.ACCOUNTING)) {
            navigate(`/workspace${WORKSPACE_ROUTES.ACCOUNTING}`);
          }
          break;
        case DeductionControlAction.REOPEN:
          handleReOpen(deduction?.id);
          break;
        case DeductionControlAction.AWAITING_RESPONSE:
        case DeductionControlAction.DISPUTE_NEEDED:
        case DeductionControlAction.DISPUTE_APPROVED:
        case DeductionControlAction.DISPUTE_REJECTED:
        case DeductionControlAction.VOID:
        case DeductionControlAction.WRITE_OFF: {
          const statusKey = STATUS_ACTION_KEYS[action];
          if (statusKey) {
            await updateDeductionStatus(deduction, statusKey);
          }
          break;
        }
        default:
          break;
      }
    },
    [
      setSelectedApproval,
      setSelectedApprovals,
      handleEditIntelligence,
      handleIntegratedDisputeRaise,
      deduction,
      setApprovalAction,
      setIsApprovalModalOpen,
      isCurrentRoute,
      handleReOpen,
      navigate,
      updateDeductionStatus,
      confirm,
    ]
  );

  const primaryControl = controls.find(c => c.type === DeductionControlType.PRIMARY);
  const menuControls = controls.filter(
    c => c.type === DeductionControlType.SECONDARY || c.type === DeductionControlType.TERTIARY
  );

  const menuItems: MenuItemProps[] = useMemo(() => {
    const workflowItems: MenuItemProps[] = menuControls
      .filter(control => control.action !== DeductionControlAction.EDIT)
      .map(control => ({
        key: control.action,
        label: ACTION_LABELS[control.action],
      }));

    const editControl = menuControls.find(control => control.action === DeductionControlAction.EDIT);
    if (!editControl) {
      return workflowItems;
    }

    const editItem: MenuItemProps = {
      key: editControl.action,
      label: ACTION_LABELS[editControl.action],
    };

    if (workflowItems.length === 0) {
      return [editItem];
    }

    return [
      ...workflowItems,
      { key: "edit-separator", separator: true },
      editItem,
    ];
  }, [menuControls]);

  const hasPrimary = Boolean(primaryControl);
  const isActionsDisabled = isDeductionsLoading || isCheckingDisputeReadiness;
  const hasMenu = menuItems.length > 0;

  if (!hasPrimary && !hasMenu) return null;

  return (
    <>
      <Flex align="center" justifyContent="space-between" className="w-100">
        {hasPrimary && (
          <Button
            size="small"
            variant="solid"
            color="primary"
            isDisabled={isActionsDisabled}
            onPress={() => void handleAction(primaryControl!.action)}
          >
            {ACTION_LABELS[primaryControl!.action]}
          </Button>
        )}

        {hasMenu && (
          <Menu
            items={menuItems}
            onSelect={key => void handleAction(key as DeductionControlAction)}
            placement="bottom end"
          >
            <Button
              size="medium"
              variant="clear"
              color="grey"
              aria-label="More actions"
              isDisabled={isActionsDisabled}
              className="rounded-xsmall"
            >
              <Icon name={Icons.DotsVertical} size="small" />
            </Button>
          </Menu>
        )}
      </Flex>

      <HasAnyPermission requiredPermissions={[WorkspacePermissions.DEDUCTIONS_DISPUTE]}>
        <RaiseDisputeModal
          key={deduction?.id}
          deduction={deduction}
          isDisputeModalOpen={isDisputeModalOpen}
          setIsDisputeModalOpen={setIsDisputeModalOpen}
          checkNumber={drawerInfo?.checkNumber ?? ""}
          claimNumber={drawerInfo?.invoiceNumber ?? ""}
          showDisputeButton={false}
          documents={deduction?.documents
            .filter(doc => doc.documentTypeKey.toLowerCase() === "backup")
            .map(doc => ({
              id: doc.id,
              name: doc.fileDisplayName,
              deletable: doc.deletable,
            }))}
          onDisputeSubmitted={handleDisputeSubmitted}
          onDisputeStatusUpdate={handleDisputeStatusUpdate}
          deductionInvoiceAmount={deduction?.totalAmount ?? deduction?.amount}
        />
      </HasAnyPermission>

      <ResponseModal
        isOpen={isDisputeConfirmationOpen}
        title="The dispute is being processed."
        message="You will receive an email, and the deduction status will be updated as soon as the dispute is filed with the purchaser."
        buttonText="OK"
        buttonOnPress={handleDisputeConfirmationClose}
        icon={Icons.CheckCircleWithBg}
        iconColor="success"
        iconClassName="without-stroke"
        closable={false}
      />

      <HasAnyPermission requiredPermissions={[WorkspacePermissions.DEDUCTIONS_SEND_APPROVAL]}>
        <SendForApprovalModal
          isOpen={isSendForApprovalModalOpen}
          onClose={() => setIsSendForApprovalModalOpen(false)}
          deduction={deduction}
        />
      </HasAnyPermission>
    </>
  );
};

export default DeductionDrawerActions;
